"use client";

import Link from "next/link";
import type { Dictionary } from "@/dictionaries/types";
import { Pagination, StatePanel } from "@/features/home-v2/ui";
import type { useArtists } from "./hooks/useArtists";
import ArtistDirectoryCard from "./ArtistDirectoryCard";
type Props = {
  lang: "fa" | "en";
  dict: Dictionary;
  query: ReturnType<typeof useArtists>;
  page: number;
  pageSize: number;
  /** The grid's column reader (`useGridColumns`), owned by the route so its page size can follow it. */
  gridRef?: (el: HTMLElement | null) => void;
  buildPageHref: (page: number) => string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

/** Presentation only: the route owns the one artists query and its URL state. */
export default function ArtistsGrid({
  lang,
  dict,
  query,
  page,
  pageSize,
  gridRef,
  buildPageHref,
  hasActiveFilters,
  onClearFilters
}: Props) {
  const isFa = lang === "fa";
  const artists = query.data?.data?.artists ?? [];
  const pageMeta = query.data?.data?.page;
  const staleAfterFailure = query.isPlaceholderData && !query.isFetching && query.failureCount > 0 && artists.length > 0;
  const retry = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => {
    void query.reset();
  }}>{dict.states.retry}</button>;
  if (!query.isLoading && artists.length === 0) {
    // A failed fetch is never shown as "no artists": that is the one
    // substitution these three branches exist to keep apart.
    if (!query.isSuccess) {
      return <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={retry} />;
    }
    if (hasActiveFilters) {
      return <StatePanel title={dict.states.filteredEmptyTitle} body={dict.states.filteredEmptyBody} action={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onClearFilters}>{dict.states.clearFilters}</button>} />;
    }
    return <StatePanel title={dict.discovery.artistsEmptyTitle} body={dict.discovery.artistsEmptyBody} action={<Link href={`/${lang}/market`} className="v2-btn v2-btn--ghost v2-focus">{dict.discovery.browseMarket}</Link>} />;
  }
  return <div className="v2-artists-results">
      {staleAfterFailure ? <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={retry} /> : null}

      <div ref={gridRef} className="v2-artists-grid" data-more={(pageMeta?.current_page ?? page) < (pageMeta?.last_page ?? 1) ? true : undefined} aria-busy={query.isFetching && !query.isPlaceholderData || undefined}>
        {query.isLoading && artists.length === 0 ? Array.from({
        length: pageSize
      }, (_, index) => <div className="v2-artists-skeleton" key={`skeleton-${index}`} aria-hidden>
                <span />
                <span />
                <span />
              </div>) : artists.map(artist => <ArtistDirectoryCard key={artist.id} artist={artist} lang={lang} personal={query.personal} />)}
      </div>

      <Pagination current={pageMeta?.current_page ?? page} last={pageMeta?.last_page ?? 1} locale={lang} label={dict.pagination?.label ?? (isFa ? "صفحه‌بندی هنرمندان" : "Artists pagination")} buildHref={buildPageHref} />
    </div>;
}